/**
 * IndexedDB-backed chunk queue for the live recording pipeline.
 *
 * Why this exists: `useLiveRecording` rotates a MediaRecorder every CYCLE_MS
 * and pushes each rotation's WebM blob over the gateway socket. If the page
 * reloads or crashes mid-consult, in-memory blobs and any unsent socket
 * frames are gone — the pre-refresh audio never reaches Whisper, the
 * transcript truncates, the doctor loses minutes of work.
 *
 * Each chunk is now durably enqueued here BEFORE the socket send and
 * marked `sent` only after the gateway accepts it. On the next mount,
 * `useLiveRecording` calls `getPending(sessionId)` and replays the queue
 * through the socket before live capture resumes — Whisper redoes the
 * transcription, but no audio is lost.
 *
 * Storage shape:
 *   db `maie-audio-chunks`, store `chunks` keyed by `[sessionId, chunkIndex]`.
 *   value: `{ sessionId, chunkIndex, blob, status, createdAt }`.
 *   index `by_session_status` on `[sessionId, status]` for the replay query.
 *
 * Quota: a 5 s Opus WebM ≈ 50–80 KB; a 30-minute consult is ~25 MB. IDB
 * quota is typically 60 % of disk on Chromium and Firefox, so this fits
 * comfortably. `gcStale` drops anything older than `STALE_AFTER_MS` on
 * each open so abandoned-but-not-cleared sessions don't pile up.
 */

const DB_NAME = 'maie-audio-chunks';
const DB_VERSION = 1;
const STORE = 'chunks';
const SESSION_STATUS_INDEX = 'by_session_status';
const STALE_AFTER_MS = 24 * 60 * 60 * 1000; // 24 h

export type ChunkStatus = 'pending' | 'sent';

export interface QueuedChunk {
  sessionId: string;
  chunkIndex: number;
  blob: Blob;
  status: ChunkStatus;
  createdAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;
let gcDone = false;

/**
 * Open (and lazily upgrade) the chunk store. Idempotent — concurrent
 * callers share the same Promise so we only wire one upgrade at a time.
 */
const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available in this environment'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: ['sessionId', 'chunkIndex'],
        });
        store.createIndex(SESSION_STATUS_INDEX, ['sessionId', 'status']);
      }
    };
    req.onsuccess = () => {
      // Re-open on version change so other tabs upgrading don't leave us
      // with a stale connection that throws on every transaction.
      req.result.onversionchange = () => {
        try {
          req.result.close();
        } catch {
          /* ignore */
        }
        dbPromise = null;
      };
      resolve(req.result);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
    req.onblocked = () => {
      // Another tab holds an older version open. Don't fail — the
      // upgrade will land once the other tab unloads.
    };
  });
  return dbPromise;
};

const tx = async (mode: IDBTransactionMode): Promise<IDBObjectStore> => {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
};

const promisify = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB request failed'));
  });

/**
 * Enqueue a chunk as `pending`. Idempotent on `[sessionId, chunkIndex]` —
 * a re-enqueue with the same key overwrites the previous row, which is
 * the right behavior if a transient failure caused the caller to retry
 * before the previous write resolved.
 */
export const enqueueChunk = async (
  sessionId: string,
  chunkIndex: number,
  blob: Blob,
): Promise<void> => {
  await ensureGc();
  const store = await tx('readwrite');
  const value: QueuedChunk = {
    sessionId,
    chunkIndex,
    blob,
    status: 'pending',
    createdAt: Date.now(),
  };
  await promisify(store.put(value));
};

/**
 * Flip a chunk's status to `sent`. Called after the gateway acknowledges
 * the audio-chunk emit. Tolerant: if the row was already evicted (gc) or
 * never enqueued, this is a no-op.
 */
export const markChunkSent = async (
  sessionId: string,
  chunkIndex: number,
): Promise<void> => {
  const store = await tx('readwrite');
  const existing = (await promisify(
    store.get([sessionId, chunkIndex]),
  )) as QueuedChunk | undefined;
  if (!existing) return;
  existing.status = 'sent';
  await promisify(store.put(existing));
};

/**
 * Read all `pending` chunks for a session, sorted by `chunkIndex` so the
 * caller can replay them in the order Whisper expects.
 */
export const getPendingChunks = async (
  sessionId: string,
): Promise<QueuedChunk[]> => {
  const store = await tx('readonly');
  const idx = store.index(SESSION_STATUS_INDEX);
  const rows = (await promisify(
    idx.getAll(IDBKeyRange.only([sessionId, 'pending'])),
  )) as QueuedChunk[];
  return rows.sort((a, b) => a.chunkIndex - b.chunkIndex);
};

/** Count of `pending` (not-yet-durable-on-server) chunks for a session.
 *  Uses the index's native count so no blob rows are materialized —
 *  cheap enough for the dock to poll while recording. Drives the
 *  "N chunks not yet on the server" backlog chip. */
export const countPendingChunks = async (
  sessionId: string,
): Promise<number> => {
  const store = await tx('readonly');
  const idx = store.index(SESSION_STATUS_INDEX);
  return promisify(idx.count(IDBKeyRange.only([sessionId, 'pending'])));
};

/** Total IDB chunk count for a session (any status). Used by the
 *  recovery flow to decide whether the source device has anything to
 *  drain when the server says AUDIO_NOT_ON_SERVER — if the count is
 *  zero we know the audio truly never made it to this device's IDB
 *  (different browser / cleared storage / wrong device). */
export const countChunks = async (sessionId: string): Promise<number> => {
  const store = await tx('readonly');
  const allKeys = (await promisify(store.getAllKeys())) as IDBValidKey[];
  return allKeys.filter(
    (key) => Array.isArray(key) && key[0] === sessionId,
  ).length;
};

/**
 * Drop every chunk for a session — both `pending` and `sent`. Called from
 * `endRecording`'s success path so a healthy completion frees the disk.
 */
export const clearSessionChunks = async (sessionId: string): Promise<void> => {
  const store = await tx('readwrite');
  // We can't delete by index range, so iterate via cursor.
  const allKeys = (await promisify(store.getAllKeys())) as IDBValidKey[];
  for (const key of allKeys) {
    if (Array.isArray(key) && key[0] === sessionId) {
      await promisify(store.delete(key));
    }
  }
};

/**
 * One-shot stale-row sweep, scoped to the lifetime of the page (so we
 * don't run the full scan on every enqueue). Drops chunks older than
 * STALE_AFTER_MS regardless of status — abandoned sessions whose end
 * never reached `clearSessionChunks` would otherwise leak indefinitely.
 */
const ensureGc = async (): Promise<void> => {
  if (gcDone) return;
  gcDone = true;
  try {
    const cutoff = Date.now() - STALE_AFTER_MS;
    const store = await tx('readwrite');
    const cursorReq = store.openCursor();
    await new Promise<void>((resolve, reject) => {
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return resolve();
        const row = cursor.value as QueuedChunk;
        if (row.createdAt < cutoff) cursor.delete();
        cursor.continue();
      };
      cursorReq.onerror = () =>
        reject(cursorReq.error ?? new Error('GC cursor failed'));
    });
  } catch {
    // GC failure must never break the live pipeline.
    gcDone = false;
  }
};
